/**
 * useFinopsCostBreakdown — hook de dados para breakdown de custo FinOps.
 *
 * Despacha para o endpoint correto de acordo com `scope`:
 *   - 'platform' → getCostBreakdown   (GET /api/v1/finops/cost-breakdown)
 *   - 'tenant'   → getTenantCostBreakdown (GET /api/v1/tenants/{id}/finops/cost-breakdown)
 *
 * Para `scope='tenant'`, `tenantId` é obrigatório; o hook fica desabilitado
 * (`enabled: false`) enquanto `tenantId` for undefined.
 *
 * staleTime:
 *   - 60 s  quando o período inclui o mês corrente (janela parcial)
 *   - 1 h   quando a janela é completamente fechada
 *
 * Chave de cache: ['finops', 'breakdown', scope, tenantId|undefined, start, end, groupBy, accountId|undefined, service|undefined]
 */
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { getCostBreakdown, getTenantCostBreakdown } from '@/api/finopsDashboard'
import type { BreakdownResponse, FinopsGroupBy } from '@/api/finopsDashboard'

export interface UseFinopsCostBreakdownParams {
  start: string                         // YYYY-MM-DD inclusivo
  end: string                           // YYYY-MM-DD exclusivo
  scope: 'platform' | 'tenant'
  tenantId?: number                     // obrigatório quando scope='tenant'
  groupBy: FinopsGroupBy
  accountId?: string
  service?: string
}

/** Retorna true se a janela [start, end) inclui o mês corrente (período parcial). */
function isPeriodPartial(end: string): boolean {
  const curMonthStart = dayjs().startOf('month')
  return dayjs(end).isAfter(curMonthStart)
}

const STALE_PARTIAL = 60_000
const STALE_CLOSED  = 3_600_000

export function useFinopsCostBreakdown({
  start,
  end,
  scope,
  tenantId,
  groupBy,
  accountId,
  service,
}: UseFinopsCostBreakdownParams) {
  const staleTime = isPeriodPartial(end) ? STALE_PARTIAL : STALE_CLOSED

  // Para escopo tenant, desabilita enquanto tenantId não estiver disponível
  const enabled = scope === 'tenant' ? typeof tenantId === 'number' : true

  return useQuery<BreakdownResponse>({
    queryKey: [
      'finops',
      'breakdown',
      scope,
      tenantId ?? null,
      start,
      end,
      groupBy,
      accountId ?? null,
      service ?? null,
    ],
    queryFn: () => {
      const period = { start, end }
      const params = { groupBy, accountId, service }

      if (scope === 'tenant') {
        return getTenantCostBreakdown(tenantId!, period, params)
      }
      return getCostBreakdown(period, params)
    },
    staleTime,
    enabled,
  })
}
